import { Group, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { type MRT_ColumnDef } from "mantine-react-table-open";
import { MantineIconCell } from "../../../mantine/mrt/components/cell/MantineIconCell";
import { type Todo } from "../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { TodosListColumnKeys } from "../TodoTable/TodoTable";

const TODO_DUE_DATE_EMPTY = "empty";
const TODO_OVERDUE = "overdue";

export function getDueDateColumn(
  todos: Array<Todo>,
  isPremium: boolean
): MRT_ColumnDef<Todo> {
  const now = new Date().valueOf();
  const allDueDateYears = todos.map(
    (todo) => backendDateOrDateTimeToLuxonDateTimeOrNull(todo.dueDate)?.year
  );
  const sortedNonNullYears = allDueDateYears
    .filter((dueDate) => typeof dueDate !== "undefined")
    .sort((dateA, dateB) => (dateB ?? 0) - (dateA ?? 0));
  const uniqueUsedDueDateYears = Array.from<number>(
    new Set(sortedNonNullYears)
  );
  const usedDueDateYears = Array.from(uniqueUsedDueDateYears);

  return {
    accessorKey: TodosListColumnKeys.DueDate,
    header: "Fälligkeitsdatum",
    enableGrouping: false,
    Cell: ({ renderedCellValue, row }) => {
      const dueDateOrNull = backendDateOrDateTimeToLuxonDateTimeOrNull(
        row.original.dueDate
      );
      const isOverdue =
        row.original.overdue ||
        ((dueDateOrNull && dueDateOrNull.valueOf() < now) ?? false);
      return (
        <div className="cell-due-date">
          <Group gap={5} wrap="nowrap">
            {isOverdue ? (
              <MantineIconCell
                iconColor="var(--mantine-color-warning-filled)"
                IconComponent={IconAlertTriangle}
                renderedCellValue={
                  <>
                    Überfällig
                    {row.original.dueDate && " (" + row.original.dueDate + ")"}
                  </>
                }
              />
            ) : (
              renderedCellValue
            )}
          </Group>
        </div>
      );
    },
    filterVariant: "multi-select",
    filterFn: (row, _, filterValue) => {
      if (filterValue.length === 0) {
        return true;
      }
      if (
        filterValue.includes(TODO_DUE_DATE_EMPTY) &&
        row.original.dueDate === null
      ) {
        return true;
      }
      if (filterValue.includes(TODO_OVERDUE)) {
        const dueDateOrNull = backendDateOrDateTimeToLuxonDateTimeOrNull(
          row.original.dueDate
        );
        const isOverdue =
          row.original.overdue ||
          ((dueDateOrNull && dueDateOrNull.valueOf() < now) ?? false);
        if (isOverdue) {
          return isOverdue;
        }
      }
      return filterValue.includes(
        backendDateOrDateTimeToLuxonDateTimeOrNull(
          row.original.dueDate
        )?.year.toString()
      );
    },
    filterTooltipValueFn: (value) =>
      value === TODO_DUE_DATE_EMPTY
        ? "(leer)"
        : value === TODO_OVERDUE
          ? "überfällig"
          : value,
    mantineFilterMultiSelectProps: {
      data: (isPremium
        ? [
            { value: TODO_DUE_DATE_EMPTY, label: "(leer)" },
            { value: TODO_OVERDUE, label: "Überfällig" }
          ]
        : [{ value: TODO_DUE_DATE_EMPTY, label: "(leer)" }]
      ).concat(
        usedDueDateYears.map((dueDate) => ({
          value: dueDate.toString(),
          label: dueDate.toString()
        }))
      ),
      searchable: false,
      dropdownOpened: true
    }
  };
}
